import { ApiType } from "@gymflow/common";
import { utcToZonedTime, zonedTimeToUtc } from "@gymflow/helpers";
import { LateCancellationItemDTO, ReportDTO } from "@gymflow/types";
import { useQuery } from "@tanstack/react-query";
import merge from "lodash/merge";

import { defaultPage } from "../defaultPage";
import { Sort } from "../Sort";
import { reportQueryKeys } from "./reportQueryKeys";

export function useLateCancellationsReport({
  api,
  startDate,
  endDate,
  page,
  limit,
  sort,
  tz,
}: {
  api: ApiType;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  sort?: Sort[];
  tz: string;
}) {
  const result = useQuery({
    queryKey: reportQueryKeys.lateCancellations({
      startDate,
      endDate,
      page,
      limit,
      sort,
      tz,
    }),
    queryFn: async () => {
      const utcStart = startDate && zonedTimeToUtc(startDate as string, tz);
      const utcEnd = endDate && zonedTimeToUtc(endDate as string, tz);
      const result = await api.memberApi.lateCancellations({
        startDate: utcStart,
        endDate: utcEnd,
        page,
        limit,
        sort,
      });
      return result.data;
    },
    placeholderData: () => {
      return {
        items: defaultPage<LateCancellationItemDTO>(),
        totalCount: 0,
      };
    },
    select: (data: ReportDTO<LateCancellationItemDTO>) => {
      const dto = merge({}, data.items, {
        content: data.items.content.map(
          ({ startTime, cancellationTime, ...rest }) => {
            return {
              startTime: startTime && utcToZonedTime(startTime, tz),
              cancellationTime:
                cancellationTime && utcToZonedTime(cancellationTime, tz),
              ...rest,
            };
          },
        ),
      });
      return {
        ...data,
        items: dto,
      };
    },
    enabled: !!startDate && !!endDate,
  });
  return result;
}
